import * as Schema from "effect/Schema";

import {
  IsoDateTime,
  NonNegativeInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

const Sha256Digest = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/));
const ExperimentDashboardCursor = Schema.String.check(Schema.isPattern(/^(?:0|[1-9]\d*)$/));

export const ExperimentEvidenceQueryInput = Schema.Struct({
  projectId: ProjectId,
  range: Schema.Literals(["30-days", "90-days", "all-history"]),
  indexRevision: Schema.optional(Schema.NullOr(Sha256Digest)),
  runCursor: Schema.optional(Schema.NullOr(ExperimentDashboardCursor)),
  descriptorCursor: Schema.optional(Schema.NullOr(ExperimentDashboardCursor)),
  evidenceCursor: Schema.optional(Schema.NullOr(ExperimentDashboardCursor)),
  matrixRunCursor: Schema.optional(Schema.NullOr(ExperimentDashboardCursor)),
  matrixMetricCursor: Schema.optional(Schema.NullOr(ExperimentDashboardCursor)),
  chartMetricKeys: Schema.optional(
    Schema.Array(TrimmedNonEmptyString).check(Schema.isMaxLength(4)),
  ),
  requestGeneration: Schema.optional(NonNegativeInt),
  comparisonCursor: Schema.optional(Schema.NullOr(ExperimentDashboardCursor)),
  comparisonSearch: Schema.optional(Schema.String.check(Schema.isMaxLength(256))),
  comparisonRunIdentities: Schema.optional(
    Schema.Array(
      Schema.Struct({
        snapshotId: TrimmedNonEmptyString,
        snapshotDigest: Sha256Digest,
        runId: TrimmedNonEmptyString,
      }),
    ).check(Schema.isMaxLength(2)),
  ),
});
export type ExperimentEvidenceQueryInput = typeof ExperimentEvidenceQueryInput.Type;

export const ExperimentFolder = Schema.Struct({
  id: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString,
  readme: Schema.NullOr(Schema.String.check(Schema.isMaxLength(256 * 1024))),
  dataFileCount: NonNegativeInt,
});
export type ExperimentFolder = typeof ExperimentFolder.Type;

export const ExperimentWorkspaceEntry = Schema.Struct({
  id: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString,
  description: Schema.NullOr(Schema.String.check(Schema.isMaxLength(4_096))),
  hasData: Schema.Boolean,
  hasDashboard: Schema.Boolean,
});
export type ExperimentWorkspaceEntry = typeof ExperimentWorkspaceEntry.Type;

const ExperimentDashboardCell = Schema.NullOr(
  Schema.Union([Schema.Number, Schema.String.check(Schema.isMaxLength(256 * 1024))]),
);

export const ExperimentDashboardTable = Schema.Struct({
  name: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4_096)),
  columns: Schema.Array(
    Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4_096)),
  ).check(Schema.isMaxLength(100)),
  rows: Schema.Array(Schema.Array(ExperimentDashboardCell).check(Schema.isMaxLength(100))).check(
    Schema.isMaxLength(2_000),
  ),
  truncated: Schema.Boolean,
});
export type ExperimentDashboardTable = typeof ExperimentDashboardTable.Type;

/**
 * Which checkout's Experiment Workspace to read: a thread's checkout, or the
 * project's Primary Checkout when `threadId` is absent or null.
 */
export const ExperimentWorkspaceQueryInput = Schema.Struct({
  projectId: ProjectId,
  threadId: Schema.optional(Schema.NullOr(ThreadId)),
  selectedExperimentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  requestGeneration: Schema.optional(NonNegativeInt),
});
export type ExperimentWorkspaceQueryInput = typeof ExperimentWorkspaceQueryInput.Type;

export const ExperimentWorkspaceQueryResult = Schema.Struct({
  projectId: ProjectId,
  revision: Sha256Digest,
  experiments: Schema.Array(ExperimentWorkspaceEntry).check(Schema.isMaxLength(500)),
  selected: Schema.NullOr(
    Schema.Struct({
      experimentId: TrimmedNonEmptyString,
      dashboardHtml: Schema.NullOr(Schema.String.check(Schema.isMaxLength(2 * 1024 * 1024))),
      data: Schema.NullOr(
        Schema.Struct({
          tables: Schema.Array(ExperimentDashboardTable).check(Schema.isMaxLength(50)),
          truncated: Schema.Boolean,
        }),
      ),
      issue: Schema.NullOr(Schema.String.check(Schema.isMaxLength(4_096))),
    }),
  ),
});
export type ExperimentWorkspaceQueryResult = typeof ExperimentWorkspaceQueryResult.Type;

export class ExperimentWorkspaceQueryError extends Schema.TaggedErrorClass<ExperimentWorkspaceQueryError>()(
  "ExperimentWorkspaceQueryError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ExperimentEvidenceSnapshot = Schema.Struct({
  snapshotId: TrimmedNonEmptyString,
  digest: Sha256Digest,
  abiVersion: Schema.Int,
  createdAt: IsoDateTime,
  supersedesSnapshotId: Schema.NullOr(TrimmedNonEmptyString),
  title: Schema.NullOr(Schema.String),
  summary: Schema.NullOr(Schema.String),
});
export type ExperimentEvidenceSnapshot = typeof ExperimentEvidenceSnapshot.Type;

export const ExperimentEvidenceRun = Schema.Struct({
  snapshotId: TrimmedNonEmptyString,
  snapshotDigest: Sha256Digest,
  runId: TrimmedNonEmptyString,
  recordedAt: IsoDateTime,
  title: Schema.NullOr(Schema.String),
  summary: Schema.NullOr(Schema.String),
});
export type ExperimentEvidenceRun = typeof ExperimentEvidenceRun.Type;

export const ExperimentMetricDescriptor = Schema.Struct({
  snapshotId: TrimmedNonEmptyString,
  snapshotDigest: Sha256Digest,
  metricKey: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
  valueKind: Schema.Literals(["number", "text"]),
  preference: Schema.Literals(["higher", "lower", "neutral"]),
  comparison: Schema.Literals(["absolute", "percent", "none"]),
  chart: Schema.Literals(["line", "bar", "none"]),
  groupKey: Schema.NullOr(Schema.String),
  unit: Schema.NullOr(Schema.String),
});
export type ExperimentMetricDescriptor = typeof ExperimentMetricDescriptor.Type;

export const ExperimentMetricObservation = Schema.Struct({
  snapshotId: TrimmedNonEmptyString,
  snapshotDigest: Sha256Digest,
  runId: TrimmedNonEmptyString,
  metricKey: TrimmedNonEmptyString,
  valueNumber: Schema.NullOr(Schema.Number),
  valueText: Schema.NullOr(Schema.String),
});
export type ExperimentMetricObservation = typeof ExperimentMetricObservation.Type;

export const ExperimentEvidenceFindingCode = Schema.Literals([
  "malformed-sqlite",
  "invalid-application-id",
  "integrity-failed",
  "missing-view",
  "abi-invalid",
  "source-unavailable",
  "source-changed",
  "provenance-unavailable",
  "identity-conflict",
  "descriptor-conflict",
]);
export type ExperimentEvidenceFindingCode = typeof ExperimentEvidenceFindingCode.Type;

export const ExperimentEvidenceFinding = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  digest: Schema.NullOr(Sha256Digest),
  code: ExperimentEvidenceFindingCode,
  metricKey: Schema.optional(TrimmedNonEmptyString),
  message: TrimmedNonEmptyString,
});
export type ExperimentEvidenceFinding = typeof ExperimentEvidenceFinding.Type;

const ExperimentEvidenceRunPage = Schema.Struct({
  items: Schema.Array(ExperimentEvidenceRun).check(Schema.isMaxLength(100)),
  visibleCount: NonNegativeInt,
  totalCount: NonNegativeInt,
  nextCursor: Schema.NullOr(ExperimentDashboardCursor),
});

const ExperimentMetricDescriptorPage = Schema.Struct({
  items: Schema.Array(ExperimentMetricDescriptor).check(Schema.isMaxLength(50)),
  visibleCount: NonNegativeInt,
  totalCount: NonNegativeInt,
  nextCursor: Schema.NullOr(ExperimentDashboardCursor),
});

const ExperimentEvidenceSnapshotPage = Schema.Struct({
  items: Schema.Array(ExperimentEvidenceSnapshot).check(Schema.isMaxLength(50)),
  visibleCount: NonNegativeInt,
  totalCount: NonNegativeInt,
  nextCursor: Schema.NullOr(ExperimentDashboardCursor),
});

const ExperimentEvidenceFindingPage = Schema.Struct({
  items: Schema.Array(ExperimentEvidenceFinding).check(Schema.isMaxLength(50)),
  visibleCount: NonNegativeInt,
  totalCount: NonNegativeInt,
  nextCursor: Schema.NullOr(ExperimentDashboardCursor),
});

export const ExperimentDashboardNumericBucket = Schema.Struct({
  startsAt: IsoDateTime,
  endsAt: IsoDateTime,
  median: Schema.NullOr(Schema.Number),
  p10: Schema.NullOr(Schema.Number),
  p90: Schema.NullOr(Schema.Number),
  observedCount: NonNegativeInt,
  missingCount: NonNegativeInt,
});
export type ExperimentDashboardNumericBucket = typeof ExperimentDashboardNumericBucket.Type;

export const ExperimentDashboardChart = Schema.Struct({
  metricKey: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
  unit: Schema.NullOr(Schema.String),
  bucketWidth: Schema.Literals(["day", "week", "month"]),
  bucketSize: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)),
  observedCount: NonNegativeInt,
  missingCount: NonNegativeInt,
  buckets: Schema.Array(ExperimentDashboardNumericBucket).check(Schema.isMaxLength(240)),
});
export type ExperimentDashboardChart = typeof ExperimentDashboardChart.Type;

export const ExperimentEvidenceQueryResult = Schema.Struct({
  projectId: ProjectId,
  experiments: Schema.Array(ExperimentFolder).check(Schema.isMaxLength(500)),
  indexRevision: Sha256Digest,
  latestIndexRevision: Sha256Digest,
  newEvidenceAvailable: Schema.Boolean,
  range: Schema.Literals(["30-days", "90-days", "all-history"]),
  exact: Schema.Literal(true),
  snapshots: ExperimentEvidenceSnapshotPage,
  runs: ExperimentEvidenceRunPage,
  descriptors: ExperimentMetricDescriptorPage,
  matrix: Schema.Struct({
    runs: Schema.Array(ExperimentEvidenceRun).check(Schema.isMaxLength(50)),
    descriptors: Schema.Array(ExperimentMetricDescriptor).check(Schema.isMaxLength(12)),
    observations: Schema.Array(ExperimentMetricObservation).check(Schema.isMaxLength(600)),
    runTotalCount: NonNegativeInt,
    descriptorTotalCount: NonNegativeInt,
    runNextCursor: Schema.NullOr(ExperimentDashboardCursor),
    descriptorNextCursor: Schema.NullOr(ExperimentDashboardCursor),
  }),
  comparisonRuns: ExperimentEvidenceRunPage,
  comparisonSelectedRuns: Schema.Array(ExperimentEvidenceRun).check(Schema.isMaxLength(2)),
  comparisonObservations: Schema.Array(ExperimentMetricObservation).check(Schema.isMaxLength(100)),
  findings: ExperimentEvidenceFindingPage,
  charts: Schema.Array(ExperimentDashboardChart).check(Schema.isMaxLength(4)),
});
export type ExperimentEvidenceQueryResult = typeof ExperimentEvidenceQueryResult.Type;

export class ExperimentEvidenceQueryError extends Schema.TaggedErrorClass<ExperimentEvidenceQueryError>()(
  "ExperimentEvidenceQueryError",
  {
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}
